import Box from '@mui/joy/Box'
import Button from '@mui/joy/Button'
import { useAtom } from 'jotai'
// @ts-ignore: No TS declaration present for a 10-year bundle
import moment from 'moment-timezone/builds/moment-timezone-with-data-10-year-range.js'
import { useTranslation } from 'react-i18next'

import type { SearchSubmissionType } from '../types'
import { ArrowForwardIcon, SearchIcon } from './icons'
import SearchModal from './search-modal'
import { searchSubmissionAtom } from '../state'
import { useGarageSearch } from '../hooks/use-garage-search'
import { getUpdatedSearchUrl } from '../helpers'

export default function SearchView({
    home,
    garagesMap,
}: {
    home?: boolean,
    garagesMap?: boolean,
}) {
    const { t, i18n } = useTranslation()
    const userLang = i18n.language

    const [searchSubmission, setSearchSubmission] = useAtom(searchSubmissionAtom)

    const {
        currentSearch,
        handleSelect,
        handleDateChange,
        handleFocusChange,
        mobileSearchDialog,
        setMobileSearchDialog,
        handleToggleMobileSearchDialog,
        desktopSearchDialog,
        setDesktopSearchDialog,
        handleToggleDesktopSearchDialog,
        handleSearchReset,
        reservationTimeRangeInvalid,
        reservationInThePast,
        reservationDays,
        reservationHours,
        selectedCETDateTime,
        currentCETDateTime,
        timeHighlight,
    } = useGarageSearch()

    const searchDisabled = reservationTimeRangeInvalid || reservationInThePast
    const showSearchErrors = !mobileSearchDialog && !desktopSearchDialog

    const handleSetSearch = (newSearch: SearchSubmissionType) => {
        setSearchSubmission(newSearch)
        setMobileSearchDialog(false)
        setDesktopSearchDialog(false)

        window.history.replaceState(
            {},
            '',
            getUpdatedSearchUrl({
                currentUrl: window.location.href,
                searchSubmission: newSearch,
                variant: 'map',
            }),
        )
    }

    const searchLink = `/${userLang}/${t('routes.garages')
        }?booking-time-from=${currentSearch.startTime ?? ''
        }&booking-time-until=${currentSearch.endTime ?? ''
        }&map_search%5Blocation%5D=${currentSearch.location ?? ''
        }&map_search%5Bbegins_at_date%5D=${currentSearch.startDate?.format('YYYY-MM-DD') ?? ''
        }&map_search%5Bbegins_at_time%5D=${currentSearch.startTime ?? ''
        }&map_search%5Bends_at_date%5D=${currentSearch.endDate?.format('YYYY-MM-DD') ?? ''
        }&map_search%5Bends_at_time%5D=${currentSearch.endTime ?? ''}`

    return (
        <>
            <Box sx={{
                ...(home ? {
                    display: 'flex',
                    '@media screen and (min-width: 66rem)': {
                        display: 'none',
                    },
                } : {
                    display: {
                        xs: 'flex',
                        md: 'none',
                    },
                    position: 'relative',
                }),
            }}>
                {garagesMap &&
                    <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        flexGrow: 1,
                        padding: '0.5rem 1rem 0.75rem',
                        fontSize: '0.875rem',
                        background: 'white',
                        boxShadow: `var(--joy-shadowRing, 0 0 #000),
                            0.3px 0.8px 1.1px rgba(187 187 187 / 0.11),
                            0.5px 1.3px 1.8px -0.6px rgba(187 187 187 / 0.18),
                            1.1px 2.7px 3.8px -1.2px rgba(187 187 187 / 0.26)`,
                        zIndex: 2,
                        cursor: 'pointer',
                    }} onClick={handleToggleMobileSearchDialog}>
                        <Box sx={{
                            flexGrow: 1,
                            paddingBottom: 0.5,
                            display: 'flex',
                            gap: 0.5,
                            justifyContent: 'center',
                            alignItems: 'center',
                        }}>
                            <SearchIcon color="#BDBDBD" />
                            <Box fontSize="0.813rem">
                                {currentSearch.location ?? t('search.defaultLocation')}
                            </Box>
                        </Box>
                        <Box display="flex" justifyContent="space-between">
                            <Box>
                                <b>{searchSubmission.startDate !== null ? moment(searchSubmission.startDate).format('DD.MM.YYYY') : '- -. - -. - - - -'}</b>
                                , {searchSubmission.startTime ?? '--:--'}
                            </Box>
                            <ArrowForwardIcon color="#BDBDBD" />
                            <Box>
                                <b>{searchSubmission.endDate !== null ? moment(searchSubmission.endDate).format('DD.MM.YYYY') : '- -. - -. - - - -'}</b>
                                , {searchSubmission.endTime ?? '--:--'}
                            </Box>
                        </Box>
                    </Box>
                }
                {garagesMap &&
                    <Box sx={{
                        position: 'absolute',
                        top: 74,
                        background: 'red',
                        width: '100%',
                        zIndex: 2,
                    }}>
                        {showSearchErrors && reservationInThePast &&
                            <Box sx={{
                                fontSize: '0.875rem',
                                textAlign: 'center',
                                padding: 1,
                            }}>
                                <Box color="white">{t('search.startInThePast')}</Box>
                            </Box>
                        }
                        {showSearchErrors && reservationTimeRangeInvalid &&
                            <Box sx={{
                                fontSize: '0.875rem',
                                textAlign: 'center',
                                padding: 1,
                            }}>
                                <Box color="white">{t('search.invalidTimeRange')}</Box>
                            </Box>
                        }
                    </Box>
                }
                {home &&
                    <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        width: '100%',
                    }}>
                        <Box sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            marginTop: '1rem',
                            fontSize: '1rem',
                            cursor: 'pointer',
                            background: 'white',
                            borderRadius: '0.25rem',
                            border: '1px solid var(--joy-palette-primary-500)',
                            boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.15)',
                            '&:hover': {
                                backgroundColor: 'secondary.100',
                            },
                            padding: '0.65rem',
                            gap: '0.20rem',
                        }} onClick={handleToggleMobileSearchDialog}>
                            <Box sx={{
                                fontSize: '0.750rem',
                                textTransform: 'uppercase',
                                fontWeight: 600,
                                color: 'primary.500',
                            }}>
                                {t('search.label')}
                            </Box>
                            <Box sx={{
                                display: 'flex',
                                gap: '0.75rem',
                            }}>
                                <Box sx={{ fontSize: '0.925rem' }}>
                                    <b>{searchSubmission.startDate !== null ? moment(searchSubmission.startDate).format('DD.MM.YYYY') : '- -. - -. - - - -'}</b>
                                    , {searchSubmission.startTime ?? '--:--'}
                                </Box>
                                <ArrowForwardIcon color="#BDBDBD" />
                                <Box sx={{ fontSize: '0.925rem' }}>
                                    <b>{searchSubmission.endDate !== null ? moment(searchSubmission.endDate).format('DD.MM.YYYY') : '- -. - -. - - - -'}</b>
                                    , {searchSubmission.endTime ?? '--:--'}
                                </Box>
                            </Box>
                        </Box>
                        <Box display="flex" flexDirection="column" alignItems="center" mt={2}>
                            <Button
                                variant="solid"
                                component="a"
                                href={searchDisabled ? undefined : searchLink}
                                disabled={searchDisabled}
                                sx={{
                                    fontSize: '1rem',
                                    width: '100%',
                                    paddingLeft: '1.5rem',
                                    paddingRight: '1.5rem',
                                    ...(!currentSearch.startDate ||
                                        !currentSearch.endDate ||
                                        reservationTimeRangeInvalid ||
                                        reservationInThePast) ? {
                                        pointerEvents: 'none',
                                        cursor: 'default',
                                        color: '#fff',
                                        backgroundColor: '#828282',
                                    } : {},
                                    '&:hover': {
                                        color: 'secondary.solidBg',
                                    },
                                }}
                            >
                                {t('search.search')}
                            </Button>
                        </Box>
                    </Box>
                }
                <SearchModal
                    searchModalOpen={mobileSearchDialog}
                    handleToggleSearchModalOpen={handleToggleMobileSearchDialog}
                    currentSearch={currentSearch}
                    modalType="mobile"
                    handleSearchReset={handleSearchReset}
                    handleSelect={handleSelect}
                    handleDateChange={handleDateChange}
                    handleFocusChange={handleFocusChange}
                    garagesMap={garagesMap}
                    reservationDays={reservationDays}
                    reservationHours={reservationHours}
                    selectedCETDateTime={selectedCETDateTime}
                    currentCETDateTime={currentCETDateTime}
                    searchLink={home ? searchLink : undefined}
                    setSearch={garagesMap ? handleSetSearch : undefined}
                    home={home}
                    timeHighlight={timeHighlight}
                />
            </Box>

            <Box sx={{
                justifyContent: 'center',
                position: 'relative',
                ...(home ? {
                    display: 'none',
                    padding: 0,
                    '@media screen and (min-width: 66rem)': {
                        display: 'flex',
                    },
                } : {
                    display: {
                        xs: 'none',
                        md: 'flex',
                    },
                }),
                ...(garagesMap ? {
                    padding: '0.5rem',
                } : {}),
            }}>
                <Box sx={{
                    display: 'flex',
                    background: 'white',
                    borderRadius: '0.25rem',
                    border: '1px solid var(--joy-palette-primary-300)',
                    ...(home ? {
                        border: '3px solid var(--joy-palette-secondary-300)',
                        boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.15)',
                    } : {}),
                }}>
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        cursor: 'pointer',
                        borderRight: '1px solid var(--joy-palette-primary-200)',
                        '&:hover': {
                            backgroundColor: 'secondary.100',
                        },
                        ...(garagesMap && desktopSearchDialog) ? {
                            zIndex: 1400,
                            backgroundColor: 'white',
                        } : {},
                        flexGrow: 1,
                        borderTopLeftRadius: '0.25rem',
                        borderBottomLeftRadius: '0.25rem',
                    }} onClick={handleToggleDesktopSearchDialog}>
                        <Box sx={{
                            display: 'flex',
                            pl: 2,
                        }}>
                            <SearchIcon color="#BDBDBD" />
                        </Box>
                        <Box sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            fontSize: '1rem',
                            px: 3,
                            py: 1.3,
                            gap: 0.4,
                        }}>
                            <Box sx={{
                                fontSize: '0.750rem',
                                textTransform: 'uppercase',
                                fontWeight: 600,
                                color: 'primary.500',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                maxWidth: 310,
                            }}>
                                {currentSearch.location ? currentSearch.location : t('search.label')}
                            </Box>
                            <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1.5,
                            }}>
                                <Box minWidth="8.25rem">
                                    <b>{currentSearch.startDate !== null ? moment(currentSearch.startDate).format('DD.MM.YYYY') : '- -. - -. - - - -'}</b>
                                    , {currentSearch.startTime ?? '--:--'}
                                </Box>
                                <ArrowForwardIcon color="#BDBDBD" />
                                <Box minWidth="8.25rem">
                                    <b>{currentSearch.endDate !== null ? moment(currentSearch.endDate).format('DD.MM.YYYY') : '- -. - -. - - - -'}</b>
                                    , {currentSearch.endTime ?? '--:--'}
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                    <Box display="flex" alignItems="center" px={2} py={1}>
                        <Button
                            variant="solid"
                            component={home ? 'a' : 'button'}
                            href={home && !searchDisabled ? searchLink : undefined}
                            disabled={searchDisabled}
                            onClick={garagesMap ? () => handleSetSearch(currentSearch) : undefined}
                            sx={{
                                ...(home ? {
                                    fontSize: '1rem',
                                } : {}),
                                paddingLeft: '1.5rem',
                                paddingRight: '1.5rem',
                                ...(!currentSearch.startDate ||
                                    !currentSearch.endDate ||
                                    reservationTimeRangeInvalid ||
                                    reservationInThePast) ? {
                                    pointerEvents: 'none',
                                    cursor: 'default',
                                    color: '#fff',
                                    backgroundColor: '#828282',
                                } : {},
                                '&:hover': {
                                    color: 'secondary.solidBg',
                                },
                            }}
                        >
                            {t('search.search')}
                        </Button>
                    </Box>
                </Box>
                <Box sx={{
                    position: 'absolute',
                    top: 90,
                    background: 'red',
                    width: '100%',
                    zIndex: 2,
                }}>
                    {showSearchErrors && reservationInThePast &&
                        <Box sx={{
                            fontSize: '0.875rem',
                            textAlign: 'center',
                            padding: 1,
                        }}>
                            <Box color="white">{t('search.startInThePast')}</Box>
                        </Box>
                    }
                    {showSearchErrors && reservationTimeRangeInvalid &&
                        <Box sx={{
                            fontSize: '0.875rem',
                            textAlign: 'center',
                            padding: 1,
                        }}>
                            <Box color="white">{t('search.invalidTimeRange')}</Box>
                        </Box>
                    }
                </Box>
                <SearchModal
                    searchModalOpen={desktopSearchDialog}
                    handleToggleSearchModalOpen={handleToggleDesktopSearchDialog}
                    currentSearch={currentSearch}
                    modalType="desktop"
                    handleSearchReset={handleSearchReset}
                    handleSelect={handleSelect}
                    handleDateChange={handleDateChange}
                    handleFocusChange={handleFocusChange}
                    garagesMap={garagesMap}
                    reservationDays={reservationDays}
                    reservationHours={reservationHours}
                    selectedCETDateTime={selectedCETDateTime}
                    currentCETDateTime={currentCETDateTime}
                    searchLink={home ? searchLink : undefined}
                    setSearch={garagesMap ? handleSetSearch : undefined}
                    home={home}
                    timeHighlight={timeHighlight}
                />
            </Box>
        </>
    )
}
